/**
 * File: web/src/lib/config.ts
 * ==========================
 *
 * Runtime configuration loader (/config.json)
 *
 * Purpose:
 * - Load environment-specific settings at runtime:
 *   apiBaseUrl, cognitoDomain, cognitoClientId, allowedTypes, etc.
 *
 * When used:
 * - Called during app startup before protected routes render.
 *
 * Learner note:
 * - CDK generates and deploys config.json to the site bucket at deploy time.
 * - This allows a single SPA build artifact to be reused in dev/prod without rebuilding.
 *
 * Gotcha:
 * - CloudFront can cache aggressively; use cache: 'no-store' so the browser requests fresh config.
 */
import type { AppConfig } from "./types";

export async function loadConfig(): Promise<AppConfig> {
  // Same-origin fetch: CloudFront serves config.json from the S3 site bucket.
  const res = await fetch("/config.json", { cache: "no-store" });

  if (!res.ok) {
    // Most common cause: stack not deployed or web/dist not present during deployment.
    throw new Error("Missing config.json. Deploy the stack to generate it.");
  }

  return res.json();
}
